(function () {
  'use strict';

  if (typeof document === 'undefined') return;

  function init(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const preview = root.dataset.preview === 'true' || Boolean(window.Shopify && Shopify.designMode);
    const formId = root.dataset.formId;
    const market = root.dataset.market === 'IE' ? 'IE' : 'GB';
    const eventPrefix = market === 'IE' ? 'ie' : 'uk';
    const experimentKey = root.dataset.experimentKey;
    const formHost = root.querySelector('[data-uk-form-host]');
    const assignmentSurface = root.querySelector('.fluk-ticket__top');
    let variant = root.dataset.variant;
    let locked = false;
    let assigned = false;
    let submitted = false;
    let exposed = false;
    let visible = false;
    let disposed = false;
    let readinessTimer;
    let readinessDeadline;
    let unsubscribeFlags;
    const capacityFull = root.dataset.capacityFull === 'true';
    const listeners = new AbortController();
    const timers = [];

    function analyticsConsent() {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return null;
      return privacy.analyticsProcessingAllowed() === true;
    }

    function analyticsAllowed() {
      return analyticsConsent() === true;
    }

    function finishAssignment() {
      locked = true;
      delete root.dataset.assignmentPending;
      assignmentSurface?.removeAttribute('aria-busy');
      clearTimeout(readinessTimer);
      clearTimeout(readinessDeadline);
      if (typeof unsubscribeFlags === 'function') unsubscribeFlags();
      unsubscribeFlags = null;
    }

    function useFallback() {
      if (locked || disposed) return;
      setVariant('control');
      finishAssignment();
    }

    function capture(name) {
      if (disposed || preview || (market === 'GB' && (!assigned || !exposed)) || !window.posthog || !analyticsAllowed()) return;
      if (posthog.has_opted_out_capturing && posthog.has_opted_out_capturing()) return;
      posthog.capture(name, { market, form_id: formId, experiment_variant: variant });
    }

    function expose() {
      if (disposed || !visible || !assigned || exposed || preview || !window.posthog || !analyticsAllowed()) return;
      if (posthog.has_opted_out_capturing && posthog.has_opted_out_capturing()) return;
      if (posthog.getFeatureFlag(experimentKey, { send_event: false }) !== variant) return;
      posthog.getFeatureFlag(experimentKey);
      exposed = true;
    }

    function setVariant(value) {
      if (!['control', 'test'].includes(value)) return;
      variant = value;
      root.dataset.variant = value;
      root.querySelectorAll('[data-uk-variant]').forEach(node => { node.hidden = node.dataset.ukVariant !== value; });
    }

    if (preview) {
      if (market === 'GB') setVariant(new URLSearchParams(location.search).get('uk_variant'));
      locked = true;
    } else if (experimentKey) {
      root.dataset.assignmentPending = 'true';
      assignmentSurface?.setAttribute('aria-busy', 'true');
      const choose = () => {
        if (locked || disposed || !root.isConnected) return;
        try {
          const consent = analyticsConsent();
          if (consent === false) { useFallback(); return; }
          // The head snippet exposes stub methods before the SDK is ready.
          if (!window.posthog || !posthog.__loaded) return;
          if (posthog.has_opted_out_capturing && posthog.has_opted_out_capturing()) { useFallback(); return; }
          if (consent === null) return;
          const value = posthog.getFeatureFlag(experimentKey, { send_event: false });
          if (value === false) { useFallback(); return; }
          if (!['control', 'test'].includes(value)) return;
          setVariant(value);
          assigned = true;
          finishAssignment();
          expose();
        } catch (_) { useFallback(); }
      };
      let subscribed = false;
      const ready = () => {
        if (locked || disposed || !root.isConnected) return;
        try {
          if (window.posthog && posthog.__loaded && !subscribed && posthog.onFeatureFlags) {
            subscribed = true;
            unsubscribeFlags = posthog.onFeatureFlags(choose);
            if (locked && typeof unsubscribeFlags === 'function') { unsubscribeFlags(); unsubscribeFlags = null; }
          }
        } catch (_) { useFallback(); }
        choose();
        if (!locked) readinessTimer = setTimeout(ready, 100);
      };
      document.addEventListener('visitorConsentCollected', choose, { signal: listeners.signal });
      readinessDeadline = setTimeout(useFallback, 10000);
      ready();
    } else {
      // No experiment running: keep the layout chosen in the theme editor, even after taps.
      locked = true;
    }

    root.addEventListener('focusin', useFallback, { signal: listeners.signal });
    root.addEventListener('pointerdown', useFallback, { signal: listeners.signal });
    root.querySelector('[data-uk-focus]')?.addEventListener('click', () => {
      visible = true;
      expose();
      capture(`${eventPrefix}_waitlist_cta_clicked`);
      timers.push(setTimeout(() => {
        const target = root.querySelector('[data-uk-form-host] input[type="email"]') || root.querySelector('.fluk-ticket');
        target?.focus({ preventScroll: true });
      }, 250));
    }, { signal: listeners.signal });

    const visibleSurfaces = new Map();
    const visibility = new IntersectionObserver(entries => {
      entries.forEach(entry => visibleSurfaces.set(entry.target, entry.isIntersecting && entry.intersectionRatio >= 0.25));
      visible = Array.from(visibleSurfaces.values()).some(Boolean);
      expose();
    }, { threshold: 0.25 });
    visibility.observe(root.querySelector('.fluk-ticket'));
    const sticky = root.querySelector('[data-uk-sticky]');
    if (sticky) visibility.observe(sticky);

    function formReady() {
      const ready = Boolean(formHost?.querySelector('form, input[type="email"], iframe'));
      const message = root.querySelector('[data-uk-provider-status]');
      if (ready && message) message.hidden = true;
      if (capacityFull && formHost) formHost.hidden = true;
      return ready;
    }
    const formObserver = new MutationObserver(formReady);
    if (formHost) {
      formObserver.observe(formHost, { childList: true, subtree: true });
      timers.push(setTimeout(() => {
        const message = root.querySelector('[data-uk-provider-status]');
        if (message && !formReady()) message.hidden = false;
      }, 10000));
    }

    window.addEventListener('omnisendForms', event => {
      const detail = event.detail;
      if (!detail || detail.form?.id !== formId || !formHost || capacityFull) return;
      const type = detail.type || detail.eventName;
      if (type === 'submit' && !submitted) {
        useFallback();
        submitted = true;
        capture(`${eventPrefix}_waitlist_form_submitted`);
        // Picked up by the "Meta Lead (UK sign-up)" custom pixel in Customer Events.
        if (!preview) {
          try { window.Shopify?.analytics?.publish?.(`flappie_${eventPrefix}_signup`, { form_id: formId, market }); } catch (_) {}
        }
      }
    }, { signal: listeners.signal });

    document.addEventListener('shopify:section:unload', event => {
      if (!event.target.contains(root)) return;
      disposed = true;
      finishAssignment();
      listeners.abort();
      visibility.disconnect();
      formObserver.disconnect();
      timers.forEach(clearTimeout);
    }, { signal: listeners.signal });
  }
  function boot() { document.querySelectorAll('[data-uk-waitlist]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
